import syllable from 'syllable';
import Sentiment from 'sentiment';
import ParaModel from 'src/paraphraser/handlers/ParaModel';
import { markLongestSubString } from './output';
import CONSTANTS, { QUILL_SETTINGS } from '../constants';
import { splitIntoSentences } from 'core/utils/sentenceSplitter';
import Levenshtein from 'core/utils/levenshtein';
import { getWordCount } from '@Common/handlers/helper';

var Stats = new (function () {
  this.updateTimer = null;
  this.sents = [];
  this.renderLCS = true;
  this.getWordSentence = text => {
    return (
      Math.round(
        (text.split(' ').filter(v => v !== '').length /
          splitIntoSentences(text).filter(s => s.type === 'Sentence').length) *
          100
      ) / 100
    );
  };

  this.getSylWord = text => {
    return Math.round((syllable(text) / text.split(' ').filter(v => v !== '').length) * 100) / 100;
  };

  this.getPercentChanged = (t1, t2) => {
    var edit = Levenshtein.get(t1, t2);
    return Math.round((edit / t1.length) * 100);
  };

  this.getSentiment = text => {
    var sentiment = new Sentiment();
    return sentiment.analyze(text).score;
  };

  this.longestCommonSubstring = (string1, string2) => {
    // Convert strings to arrays to treat unicode symbols length correctly.
    // For example:
    // '𐌵'.length === 2
    // [...'𐌵'].length === 1
    const s1 = [...string1];
    const s2 = [...string2];

    // Init the matrix of all substring lengths to use Dynamic Programming approach.
    const substringMatrix = Array(s2.length + 1)
      .fill(null)
      .map(() => {
        return Array(s1.length + 1).fill(null);
      });

    // Fill the first row and first column with zeros to provide initial values.
    for (let columnIndex = 0; columnIndex <= s1.length; columnIndex += 1) {
      substringMatrix[0][columnIndex] = 0;
    }

    for (let rowIndex = 0; rowIndex <= s2.length; rowIndex += 1) {
      substringMatrix[rowIndex][0] = 0;
    }

    // Build the matrix of all substring lengths to use Dynamic Programming approach.
    let longestSubstringLength = 0;
    let longestSubstringColumn = 0;
    let longestSubstringRow = 0;

    for (let rowIndex = 1; rowIndex <= s2.length; rowIndex += 1) {
      for (let columnIndex = 1; columnIndex <= s1.length; columnIndex += 1) {
        if (s1[columnIndex - 1] === s2[rowIndex - 1]) {
          substringMatrix[rowIndex][columnIndex] =
            substringMatrix[rowIndex - 1][columnIndex - 1] + 1;
        } else {
          substringMatrix[rowIndex][columnIndex] = 0;
        }

        // Try to find the biggest length of all common substring lengths
        // and to memorize its last character position (indices)
        if (substringMatrix[rowIndex][columnIndex] > longestSubstringLength) {
          longestSubstringLength = substringMatrix[rowIndex][columnIndex];
          longestSubstringColumn = columnIndex;
          longestSubstringRow = rowIndex;
        }
      }
    }

    if (longestSubstringLength === 0) {
      // Longest common substring has not been found.
      return '';
    }

    // Detect the longest substring from the matrix.
    let longestSubstring = '';
    var overload = 0;
    while (substringMatrix[longestSubstringRow][longestSubstringColumn] > 0) {
      overload += 1;
      if (overload > 10000) {
        break;
      }
      longestSubstring = s1[longestSubstringColumn - 1] + longestSubstring;
      longestSubstringRow -= 1;
      longestSubstringColumn -= 1;
    }
    return longestSubstring;
  };

  this.getGradeLevel = text => {
    var wordCount = text.split(' ').length;
    var avgSyl = syllable(text) / wordCount;
    var ratio = (1 / wordCount) * 100;
    var sentences = splitIntoSentences(text).filter(s => s.type === 'Sentence').length;
    var avgSentences = sentences * ratio;
    var y = avgSentences;
    var x = avgSyl * 100;
    if (y > 0.275 * x - 20.25) return '100%';
    else if (y > 0.45 * x - 44.2) return '93%';
    else if (y > 0.42 * x - 42.1) return '87%';
    else if (y > 0.22 * x - 17.5) return '80%';
    else if (y > 0.11 * x - 6.94) return '73%';
    else if (y > 0.083333333 * x - 5.133333) return '67%';
    else if (y > 0.180556 * x - 17.8056) return '60%';
    else if (y > 0.125 * x - 12.65) return '53%';
    else if (y > 0.15 * x - 17.7) return '47%';
    else if (y > 0.26 * x - 34.94) return '40%';
    else if (y > 0.16666667 * x - 22) return '33%';
    else if (y > 0.2833333 * x - 41.7) return '27%';
    else if (y > 0.3 * x - 45.6) return '20%';
    else if (y > 0.5 * x - 81) return '13%';
    else return '7%';
  };

  this.calcLargeLCS = orig => {
    this.renderLCS = false;
    var all_words = [];
    ParaModel.paraphrasers.forEach(p => {
      var firstWord = true;
      p.focusedOutput.getWords().forEach(w => {
        w.firstWord = firstWord;
        w.postCitation = p.postCitation;
        all_words.push(w);
        firstWord = false;
      });
    });
    this.sents = this.makeSentsFromWords(all_words);
    return '-';
  };

  this.calcLCS = orig => {
    if (orig.length >= 2500) {
      return this.calcLargeLCS(orig);
    }

    this.renderLCS = true;
    var all_words = [];
    ParaModel.paraphrasers.forEach(p => {
      var firstWord = true;
      var copy = p.focusedOutput.getWords().map(w => {
        var data = {
          index: w.index,
          word: w.word,
          isLoading: w.isLoading,
          under: w.under,
          inLongestSubstr: false,
          lcsRightOffset: 0,
          lcsLeftOffset: 0,
          firstWord: firstWord,
          different: w.different,
          autoflipped: w.autoflipped,
          seg: w.seg,
          postCitation: p.postCitation,
          //tfidf
          //diff
        };
        firstWord = false;
        return data;
      });
      all_words = all_words.concat(copy);
    });

    var new_words = markLongestSubString(orig, all_words);
    var lcs = new_words.filter(w => w.inLongestSubstr);
    this.sents = this.makeSentsFromWords(new_words);
    var tilda = orig.length > 2000 ? '~' : '';
    return tilda + lcs.map(x => x.word).join(' ').length;
  };

  this.makeSentsFromWords = new_words => {
    var sents = [];
    var cur_sent = [];
    new_words.forEach(w => {
      if (w.firstWord) {
        sents.push(cur_sent);
        cur_sent = [];
      }
      cur_sent.push(w);
    });
    sents.push(cur_sent);
    sents.shift();
    return sents;
  };

  this.clearStats = () => {
    this.origWordRatio = '-';
    this.modWordRatio = '-';
    this.origSylRatio = '-';
    this.modSylRatio = '-';
    this.origGradeLevel = '-';
    this.modGradeLevel = '-';
    this.origWordCount = '-';
    this.modWordCount = '-';
    this.origCharCount = '-';
    this.modCharCount = '-';
    this.percChanged = '-';
    this.longestCommonLen = '-';
    this.sents = [];
  };
  this.clearStats();

  this.updateStats = (cb = null) => {
    //console.log("updating stats..")
    var origText = ParaModel.getOriginalArticle();
    var modText = ParaModel.getModifiedArticleOnParaphrasers();
    if (!origText || !modText) {
      return;
    }

    /* if (ParaModel.mode.isExtension) {
      // this.calcLargeLCS(origText); // just a hack to load the sentences on initial load.
      this.origWordRatio = this.getWordSentence(origText);
      this.modWordRatio = this.getWordSentence(modText);
      this.origSylRatio = this.getSylWord(origText);
      this.modSylRatio = this.getSylWord(modText);
      this.origGradeLevel = this.getGradeLevel(origText);
      this.modGradeLevel = this.getGradeLevel(modText);
      this.origWordCount = origText.split(' ').filter(v => v !== '').length;
      this.modWordCount = modText.split(' ').filter(v => v !== '').length;
      this.percChanged = this.getPercentChanged(origText, modText);
      this.longestCommonLen = this.longestCommonSubstring(origText, modText).length;
    } else { */
    if (ParaModel.calcStatsRealTime) {
      if (this.updateTimer) {
        clearTimeout(this.updateTimer);
      }
      this.updateTimer = setTimeout(() => {
        if (origText.length < CONSTANTS.STATS_CALC_LIMIT) {
          this.origWordRatio = this.getWordSentence(origText);
          this.modWordRatio = this.getWordSentence(modText);
          this.origSylRatio = this.getSylWord(origText);
          this.modSylRatio = this.getSylWord(modText);
          this.origGradeLevel = this.getGradeLevel(origText);
          this.modGradeLevel = this.getGradeLevel(modText);
          this.origWordCount = getWordCount(origText);
          this.modWordCount = getWordCount(modText);
          this.origCharCount = origText.length;
          this.modCharCount = modText.length;
          this.percChanged = this.getPercentChanged(origText, modText);
        }
        if (cb) cb();
      }, 1000);
    }
    this.longestCommonLen = this.calcLCS(origText);
    // }
    // ParaModel.update('updating stats');
    // ParaModel.adjustInputOutputHeights(true);
  };
})();

export default Stats;
